import { forwardRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";

/**
 * Input numerico che accetta sia "," sia "." come separatore decimale.
 * - Mostra sempre il valore con virgola (convenzione italiana)
 * - Emette via onChange un evento con value normalizzato (stringa con "." per parseFloat)
 *   ma il <input> stesso mostra l'inserimento dell'utente.
 *
 * Drop-in replacement di <Input type="number" step="0.01" ...>
 */
const AmountInput = forwardRef(function AmountInput({ value, defaultValue, onChange, ...rest }, ref) {
  const isControlled = value !== undefined;
  const initialDisplay = formatForDisplay(isControlled ? value : defaultValue);
  const [display, setDisplay] = useState(initialDisplay);

  // Sync external value (controlled mode) when it changes externally
  useEffect(() => {
    if (!isControlled) return;
    const next = formatForDisplay(value);
    setDisplay((cur) => (cur === next || replaceComma(cur) === replaceComma(next) ? cur : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    let raw = e.target.value;
    // Allow only digits, comma, dot and a leading minus
    raw = raw.replace(/[^\d.,-]/g, "");
    // Only keep first separator: collapse multiple dots/commas to a single comma
    const firstSep = raw.search(/[.,]/);
    if (firstSep !== -1) {
      const before = raw.slice(0, firstSep);
      const after = raw.slice(firstSep + 1).replace(/[.,]/g, "");
      raw = before + "," + after;
    }
    setDisplay(raw);
    if (onChange) {
      // Emit normalized value with dot, ready for parseFloat
      const normalized = raw.replace(",", ".");
      onChange({ ...e, target: { ...e.target, value: normalized } });
    }
  };

  const handleBlur = (e) => {
    // Normalize trailing comma like "10," -> "10"
    const trimmed = display.replace(/[.,]$/, "");
    if (trimmed !== display) setDisplay(trimmed);
    if (rest.onBlur) {
      // Espone al parent l'evento con target.value normalizzato (dot decimal),
      // così eventuali `parseFloat(ev.target.value)` non troncano su "10,50".
      const normalized = trimmed.replace(",", ".");
      const patched = { ...e, target: { ...e.target, value: normalized } };
      rest.onBlur(patched);
    }
  };

  // Fallback: alcune tastiere mobili (inputmode=decimal) filtrano il carattere
  // "," a livello browser. Intercettiamo il keydown per iniettare il separatore
  // manualmente, così l'utente può SEMPRE inserire un decimale.
  const handleKeyDown = (e) => {
    if (e.key === "," || e.key === ".") {
      // Se il display non contiene già un separatore, aggiungiamolo esplicitamente
      if (!/[.,]/.test(display)) {
        e.preventDefault();
        const target = e.currentTarget;
        const pos = target.selectionStart ?? display.length;
        const next = display.slice(0, pos) + "," + display.slice(pos);
        setDisplay(next);
        if (onChange) {
          const normalized = next.replace(",", ".");
          onChange({ ...e, target: { ...target, value: normalized } });
        }
      }
    }
    if (rest.onKeyDown) rest.onKeyDown(e);
  };

  return (
    <Input
      ref={ref}
      type="text"
      // NB: NON impostiamo inputMode="decimal": su alcune tastiere Android quel
      // modo mostra un tastierino numerico che NON offre il tasto virgola
      // (blocca l'inserimento del separatore decimale a livello OS).
      // Usiamo la tastiera testuale standard, che ha sempre la virgola in tutti i layout.
      autoComplete="off"
      // Hint per browser / autofill: valore numerico con separatore decimale
      pattern="[0-9]*[.,]?[0-9]*"
      {...rest}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
});

function formatForDisplay(v) {
  if (v === undefined || v === null || v === "") return "";
  // Formatta a 2 decimali (convenzione italiana) SOLO se il valore arriva come number
  // dal backend (es. 10.5 -> "10,50"). Se è una stringa (digitazione live in modalità
  // controlled o valore parziale), NON riformattare: preserva l'input dell'utente.
  if (typeof v === "number" && isFinite(v)) {
    return v.toFixed(2).replace(".", ",");
  }
  return String(v).replace(".", ",");
}
function replaceComma(s) { return String(s).replace(",", "."); }

export default AmountInput;
